import { useCallback, useEffect, useState } from 'react'
import api from '../api/client'
import { articulosApi } from '../api/notasVenta'
import Boton from '../components/Boton'
import Modal from '../components/Modal'
import SelectorArticulosModal from '../components/SelectorArticulosModal'
import { colors, font, radius, space, sx, text } from '../design/tokens'

/**
 * Wine Club — la composición del mes: qué vinos lleva cada nivel.
 *
 * Es la curaduría, y alimenta dos cosas: la nota de venta de cada renovación y
 * la página del nivel en la tienda. Solo una composición CONFIRMADA sirve para
 * las dos — mientras está en borrador es trabajo en curso.
 */

const MESES = ['enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio',
  'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre']

/** "2026-09-01" → "septiembre 2026" */
function etiquetaMes(iso) {
  const [a, m] = iso.split('-')
  return `${MESES[parseInt(m, 10) - 1]} ${a}`
}

/** "2026-09-01" a partir de año y mes (0-11), sin pasar por zonas horarias. */
function iso(anio, mes0) {
  return `${anio}-${String(mes0 + 1).padStart(2, '0')}-01`
}

/** Primer día del mes actual. */
function mesActual() {
  const d = new Date()
  return iso(d.getFullYear(), d.getMonth())
}

/**
 * Corre un período n meses. Se parsea a mano en vez de `new Date("2026-09-01")`
 * porque esa forma se interpreta en UTC y en Argentina cae en el mes anterior.
 */
function correrMes(periodo, n) {
  const [a, m] = periodo.split('-').map(Number)
  const d = new Date(a, m - 1 + n, 1)
  return iso(d.getFullYear(), d.getMonth())
}

const ESTADOS = {
  confirmada: { texto: 'Confirmada', color: colors.ok, bg: colors.okBg },
  borrador: { texto: 'Borrador', color: colors.warn, bg: colors.warnBg },
  sin_armar: { texto: 'Sin armar', color: colors.textSec, bg: colors.bgSoft },
}

function Estado({ estado }) {
  const e = ESTADOS[estado] || ESTADOS.sin_armar
  return (
    <span style={{
      background: e.bg, color: e.color, padding: '2px 8px', borderRadius: radius.xs,
      fontSize: text.meta, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em',
    }}>{e.texto}</span>
  )
}

export default function WineClubComposicionPage() {
  const [mes, setMes] = useState(mesActual())
  const [niveles, setNiveles] = useState([])
  const [cargando, setCargando] = useState(true)
  const [error, setError] = useState(null)
  const [aviso, setAviso] = useState(null)
  const [guardando, setGuardando] = useState(null)
  // { nivel } del que se está completando con el buscador
  const [buscando, setBuscando] = useState(null)
  const [historico, setHistorico] = useState(null)
  // { nivel } del que se está cargando pegando una lista de códigos
  const [pegando, setPegando] = useState(null)
  const [textoPegado, setTextoPegado] = useState('')
  const [resolviendo, setResolviendo] = useState(false)

  const cargar = useCallback(async () => {
    setCargando(true)
    setError(null)
    try {
      const { data } = await api.get('/wine-club/composiciones', { params: { mes } })
      setNiveles(data.niveles || [])
    } catch (e) {
      setError(e.response?.data?.message || 'No se pudo cargar la composición del mes.')
    } finally {
      setCargando(false)
    }
  }, [mes])

  useEffect(() => { cargar() }, [cargar])

  const setItems = (nivel, items) =>
    setNiveles(ns => ns.map(n => (n.nivel === nivel ? { ...n, items, estado: 'borrador' } : n)))

  const quitar = (nivel, idx) => {
    const n = niveles.find(x => x.nivel === nivel)
    setItems(nivel, n.items.filter((_, i) => i !== idx))
  }

  const elegir = (articulo) => {
    const { nivel } = buscando
    const n = niveles.find(x => x.nivel === nivel)
    if (n.items.some(i => i.articulo_id === articulo.id)) {
      setAviso({ tipo: 'err', texto: 'Ese vino ya está en la composición de ' + n.nombre + '.' })
      setBuscando(null)
      return
    }
    setItems(nivel, [...n.items, {
      articulo_id: articulo.id,
      codigo_alfa: articulo.codigo_alfa,
      descripcion: articulo.descripcion,
    }])
    setBuscando(null)
  }

  /**
   * Pegar una lista de códigos (una columna de Excel, uno por línea) y que se
   * resuelvan contra el maestro. Si una columna trae también la descripción, se
   * usa solo la primera: el código es lo que identifica.
   *
   * Los que no aparecen NO se agregan y se avisan por código, para poder
   * corregirlos: agregar el que no es sería peor que no agregar nada.
   */
  const procesarPegado = async () => {
    const codigos = textoPegado
      .split(/\r?\n/)
      .map(l => l.split('\t')[0].trim())
      .filter(Boolean)

    if (codigos.length === 0) {
      setAviso({ tipo: 'err', texto: 'Pegá al menos un código.' })
      return
    }

    setResolviendo(true)
    try {
      const encontrados = []
      const faltantes = []

      const res = await Promise.allSettled(codigos.map(c => articulosApi.buscar(c)))
      res.forEach((r, i) => {
        const codigo = codigos[i]
        const lista = r.status === 'fulfilled' ? (r.value.data?.data || r.value.data || []) : []
        const art = lista.find(a => (a.codigo_alfa || '').toLowerCase() === codigo.toLowerCase())
        art ? encontrados.push(art) : faltantes.push(codigo)
      })

      const n = niveles.find(x => x.nivel === pegando.nivel)
      const yaEstan = new Set(n.items.map(i => i.articulo_id))
      const nuevos = encontrados
        .filter(a => !yaEstan.has(a.id))
        .map(a => ({ articulo_id: a.id, codigo_alfa: a.codigo_alfa, descripcion: a.descripcion }))

      if (nuevos.length) setItems(pegando.nivel, [...n.items, ...nuevos])

      setPegando(null)
      setAviso(faltantes.length
        ? { tipo: 'err', texto: `Agregué ${nuevos.length}. No encontré: ${faltantes.join(', ')}.` }
        : { tipo: 'ok', texto: `Agregué ${nuevos.length} vinos a ${n.nombre}. Acordate de guardar.` })
    } finally {
      setResolviendo(false)
    }
  }

  const accion = async (nivel, ruta, cuerpo, exito) => {
    setGuardando(nivel)
    setAviso(null)
    try {
      const { data } = await api.post(ruta, cuerpo)
      setAviso({ tipo: 'ok', texto: data.mensaje || exito })
      await cargar()
    } catch (e) {
      const err = e.response?.data
      const primero = err?.errors ? Object.values(err.errors)[0]?.[0] : null
      setAviso({ tipo: 'err', texto: primero || err?.message || 'No se pudo guardar.' })
    } finally {
      setGuardando(null)
    }
  }

  const guardar = (n) => accion(n.nivel, '/wine-club/composiciones', {
    mes, nivel: n.nivel, articulos: n.items.map(i => i.articulo_id), nota: n.nota || null,
  }, `Se guardó ${n.nombre} como borrador.`)

  const confirmar = (n) => accion(n.nivel, '/wine-club/composiciones/confirmar',
    { mes, nivel: n.nivel }, `${n.nombre} quedó confirmada: ya se publica y factura.`)

  const duplicar = () => accion('_todos', '/wine-club/composiciones/duplicar', { mes }, 'Listo.')

  const verHistorico = async (nivel) => {
    try {
      const { data } = await api.get('/wine-club/composiciones/historico', { params: { nivel } })
      setHistorico(data)
    } catch {
      setAviso({ tipo: 'err', texto: 'No se pudo traer el histórico.' })
    }
  }

  return (
    <div style={{ padding: space['3xl'], fontFamily: font.sans, color: colors.text, maxWidth: 1100 }}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: space.lg, flexWrap: 'wrap' }}>
        <h1 style={{ margin: 0, fontSize: 22, fontWeight: 600 }}>Wine Club — composición del mes</h1>
        <span style={{ fontSize: text.sec, color: colors.textSec }}>
          Los vinos de cada nivel. Se publican en la página de la membresía y arman la venta de cada renovación.
        </span>
      </div>

      {/* Mes + acciones del mes */}
      <div style={{ display: 'flex', alignItems: 'center', gap: space.md, margin: `${space['2xl']}px 0 ${space.lg}px` }}>
        <Boton variante="ghost" sm onClick={() => setMes(correrMes(mes, -1))}>← Mes anterior</Boton>

        <span style={{ ...sx.mono, fontSize: text.title, fontWeight: 600, minWidth: 160, textAlign: 'center' }}>
          {etiquetaMes(mes)}
        </span>

        <Boton variante="ghost" sm onClick={() => setMes(correrMes(mes, 1))}>Mes siguiente →</Boton>

        <div style={{ flex: 1 }} />
        <Boton variante="secondary" sm onClick={duplicar} disabled={guardando === '_todos'}>
          Copiar del mes anterior
        </Boton>
      </div>

      {aviso && (
        <div style={{
          background: aviso.tipo === 'ok' ? colors.okBg : colors.errBg,
          color: aviso.tipo === 'ok' ? colors.ok : colors.err,
          border: `1px solid ${aviso.tipo === 'ok' ? colors.ok : colors.err}22`,
          padding: `${space.md}px ${space.lg}px`, borderRadius: radius.md,
          fontSize: text.body, marginBottom: space.lg,
        }}>{aviso.texto}</div>
      )}

      {error && (
        <div style={{ background: colors.errBg, color: colors.err, padding: `${space.md}px ${space.lg}px`, borderRadius: radius.md, fontSize: text.body }}>
          {error}
        </div>
      )}

      {cargando ? (
        <p style={{ color: colors.textSec, fontSize: text.body }}>Cargando…</p>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: space.lg }}>
          {niveles.map(n => {
            const completo = n.items.length === n.botellas_esperadas
            return (
              <div key={n.nivel} style={{ ...sx.card, padding: space.xl }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: space.md, marginBottom: space.lg }}>
                  <h2 style={{ margin: 0, fontSize: text.title, fontWeight: 600 }}>{n.nombre}</h2>
                  <span style={{ ...sx.mono, fontSize: text.sec, color: colors.textMeta }}>{n.sku}</span>
                  <Estado estado={n.estado} />
                  <span style={{ fontSize: text.sec, color: completo ? colors.textSec : colors.warn }}>
                    {n.items.length} de {n.botellas_esperadas} vinos
                  </span>
                  <div style={{ flex: 1 }} />
                  <Boton variante="ghost" sm onClick={() => verHistorico(n.nivel)}>Meses anteriores</Boton>
                </div>

                {/* Los vinos elegidos */}
                {n.items.length === 0 ? (
                  <p style={{ color: colors.textMeta, fontSize: text.body, margin: `0 0 ${space.lg}px` }}>
                    Todavía no elegiste los vinos de este mes.
                  </p>
                ) : (
                  <table style={{ ...sx.table, marginBottom: space.lg }}>
                    <tbody>
                      {n.items.map((it, i) => (
                        <tr key={it.articulo_id} style={{ borderBottom: `1px solid ${colors.borderSoft}` }}>
                          <td style={{ padding: `${space.md}px 0`, width: 28, color: colors.textMeta, fontSize: text.sec }}>{i + 1}</td>
                          <td style={{ ...sx.mono, padding: `${space.md}px ${space.lg}px ${space.md}px 0`, fontSize: text.sec, color: colors.textSec, whiteSpace: 'nowrap' }}>
                            {it.codigo_alfa}
                          </td>
                          <td style={{ padding: `${space.md}px 0`, fontSize: text.body }}>{it.descripcion}</td>
                          <td style={{ padding: `${space.md}px 0`, textAlign: 'right', width: 100 }}>
                            <Boton variante="destructive" sm onClick={() => quitar(n.nivel, i)}>Quitar</Boton>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}

                <div style={{ display: 'flex', alignItems: 'center', gap: space.md, flexWrap: 'wrap' }}>
                  <Boton variante="secondary" sm onClick={() => setBuscando({ nivel: n.nivel })}>
                    + Agregar vino
                  </Boton>
                  <Boton variante="secondary" sm onClick={() => { setPegando({ nivel: n.nivel }); setTextoPegado('') }}>
                    Pegar lista
                  </Boton>

                  <div style={{ flex: 1 }} />

                  {/* Guardar es LA acción de la card, así que es la azul. Antes la
                      azul era "Confirmar" —que está deshabilitada hasta tener los
                      vinos completos— y se leía como que la pantalla no dejaba
                      guardar: apretabas el botón que más se ve y no pasaba nada. */}
                  <Boton variante="primary" sm onClick={() => guardar(n)} disabled={guardando === n.nivel || n.items.length === 0}>
                    {guardando === n.nivel ? 'Guardando…' : 'Guardar'}
                  </Boton>

                  {n.estado === 'confirmada' ? (
                    <span style={{
                      background: colors.okBg, color: colors.ok, padding: '4px 12px',
                      borderRadius: radius.sm, fontSize: text.sec, fontWeight: 600,
                    }}>✓ Confirmada</span>
                  ) : completo ? (
                    <Boton variante="success" sm onClick={() => confirmar(n)} disabled={guardando === n.nivel}>
                      ✓ Confirmar
                    </Boton>
                  ) : null}
                </div>

                {/* Por qué todavía no se puede confirmar, en vez de un botón
                    apagado que no explica nada. */}
                {n.estado !== 'confirmada' && (
                  <p style={{ margin: `${space.md}px 0 0`, fontSize: text.sec, color: colors.textSec }}>
                    {completo
                      ? 'Guardá y después confirmá: recién ahí se publica en la web y sirve para facturar las renovaciones.'
                      : `Cuando estén los ${n.botellas_esperadas} vinos vas a poder confirmarla.`}
                  </p>
                )}
              </div>
            )
          })}
        </div>
      )}

      {buscando && (
        <SelectorArticulosModal
          modo="simple"
          onSelect={elegir}
          onClose={() => setBuscando(null)}
        />
      )}

      {pegando && (
        <Modal title={`Pegar lista — ${niveles.find(n => n.nivel === pegando.nivel)?.nombre}`}
          onClose={() => setPegando(null)} maxWidth="560px">
          <p style={{ margin: `0 0 ${space.lg}px`, fontSize: text.body, color: colors.textSec }}>
            Pegá la columna de códigos desde Excel, uno por línea. Si copiás más de una
            columna se usa la primera.
          </p>
          <textarea
            value={textoPegado}
            onChange={e => setTextoPegado(e.target.value)}
            autoFocus
            rows={8}
            placeholder={'AL02713\nBE34652\n…'}
            style={{
              width: '100%', fontFamily: font.mono, fontSize: text.body,
              padding: space.lg, borderRadius: radius.sm,
              border: `1px solid ${colors.border}`, resize: 'vertical',
            }}
          />
          <div style={{ display: 'flex', gap: space.md, marginTop: space.lg, justifyContent: 'flex-end' }}>
            <Boton variante="ghost" onClick={() => setPegando(null)}>Cancelar</Boton>
            <Boton variante="primary" onClick={procesarPegado} disabled={resolviendo}>
              {resolviendo ? 'Buscando…' : 'Agregar'}
            </Boton>
          </div>
        </Modal>
      )}

      {historico && (
        <div style={{
          position: 'fixed', inset: 0, background: colors.overlay, display: 'flex',
          alignItems: 'center', justifyContent: 'center', zIndex: 60, padding: space.xl,
        }}>
          <div style={{ ...sx.card, maxWidth: 620, width: '100%', maxHeight: '80vh', overflowY: 'auto' }}>
            <div style={{ display: 'flex', alignItems: 'center', marginBottom: space.lg }}>
              <h2 style={{ margin: 0, fontSize: text.title, fontWeight: 600 }}>
                {historico.nivel} — meses anteriores
              </h2>
              <div style={{ flex: 1 }} />
              <Boton variante="ghost" sm onClick={() => setHistorico(null)}>Cerrar</Boton>
            </div>

            {historico.meses.length === 0 ? (
              <p style={{ color: colors.textMeta, fontSize: text.body }}>
                Todavía no hay meses confirmados de este nivel.
              </p>
            ) : historico.meses.map(m => (
              <div key={m.periodo} style={{ marginBottom: space.xl }}>
                <div style={{ ...sx.cardTitle, marginBottom: space.sm }}>{m.etiqueta}</div>
                {m.items.map((i, k) => (
                  <div key={k} style={{ fontSize: text.body, padding: '3px 0' }}>
                    <span style={{ ...sx.mono, fontSize: text.sec, color: colors.textSec, marginRight: space.md }}>{i.codigo_alfa}</span>
                    {i.descripcion}
                  </div>
                ))}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}
